import React, {useRef, useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import SMCTableCheckAll from './SMCTableHeaderDivCheckAll';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { ClickAwayListener} from '@mui/material';
import {makeStyles} from '@mui/styles'
import SMCTableHeaderMenu from './SMCTableHeaderMenu';
import SMCTableHeaderColumn from './SMCTableHeaderColumn';

function SMCTableHeader ({
    innerRef,
    innerWidth,
    outerWidth,
    height,
    tableFullHeight,
    isAllChecked,
    hasCheckboxColumn=true,
    hasCheckAll=false,
    checkAllOnChange,
    columns,
    isColumnsResizable,
    columnsWidths,
    setColumnsWidths,
    setDefaultWidths,
    onEndResizingColumns,
    columnsState,
    setColumnsState,
    setDefaultColumnsState,
    sortBasis,
    sortDirection,
    setSort
}){
    const classes = useStyles({ height });

    const MIN_COLUMN_WIDTH = 20;

    const [isEditTableProfileBoxOpen, setIsEditTableProfileBoxOpen] = useState(false);

    const isResizing = useRef(false);
    const lastDownX = useRef(0);
    const resizingLeftHeaderId = useRef();
    const resizingRightHeaderId = useRef();

    // Follow mouse movements, and handle the resizing of columns
    useEffect(() => {
        if (isColumnsResizable) {
            const mouseMoveListener = e => {
                if (isResizing.current) {
                    const offset = e.clientX - lastDownX.current;
                    if (offset) {
                        const newColumnsWidths = [...columnsWidths];
                        const leftColumnWidthObj = newColumnsWidths.find(colWidth => colWidth.id === resizingLeftHeaderId.current);
                        const rightColumnWidthObj = newColumnsWidths.find(colWidth => colWidth.id === resizingRightHeaderId.current);

                        // if (leftColumnWidthObj.width + offset < MIN_COLUMN_WIDTH || rightColumnWidthObj.width - offset < MIN_COLUMN_WIDTH) {
                        if (leftColumnWidthObj.width + offset < MIN_COLUMN_WIDTH) {
                            return;
                        }
                        leftColumnWidthObj.width += offset;
                        if (rightColumnWidthObj.width - offset < MIN_COLUMN_WIDTH) {
                            rightColumnWidthObj.width = MIN_COLUMN_WIDTH;
                        }
                        else {
                            rightColumnWidthObj.width -= offset;
                        }
                        setColumnsWidths(newColumnsWidths);
                        lastDownX.current = e.clientX;
                    }
                }
            };

            document.addEventListener('mousemove', mouseMoveListener);
            return (() => document.removeEventListener('mousemove', mouseMoveListener));
        }
    }, [columnsWidths, setColumnsWidths, isColumnsResizable]);

    // End resizing on mouse up
    useEffect(() => {
        if (isColumnsResizable) {
            const mouseUpListener = () => {
                if (isResizing.current) {
                    isResizing.current = false;
                    onEndResizingColumns && onEndResizingColumns(columnsWidths);
                }
            };
            document.addEventListener('mouseup', mouseUpListener);
            return () => document.removeEventListener('mouseup', mouseUpListener);
        }
    }, [isColumnsResizable, onEndResizingColumns, columnsWidths]);

    const header = useMemo(() => {
        if (Array.isArray(columnsState)) {
            const shownColumns = columnsState.filter(colState => (colState.id && colState.show));
            return (
                shownColumns.map((colState, index) => {
                    const column = columns.find(col => col.id === colState.id);
                    if (!column) {
                        return null;
                    }
                    const width = columnsWidths ? columnsWidths.find(col => col.id === column.id)?.width : column.width;
                    return (
                        <React.Fragment key={column.id}>
                        <SMCTableHeaderColumn
                            column={column}
                            width={width}
                            setSort={setSort}
                            sortBasis={sortBasis}
                            sortDirection={sortDirection}
                            className={classes.cell}
                        />
                        {
                            // Cell separators, if isColumnsResizable, they are draggable too
                            index !== (shownColumns.length - 1) && (
                                <td
                                    className={clsx(classes.cellSeparator, isColumnsResizable && classes.resizable)}
                                    onMouseDown={e => {
                                        if (isColumnsResizable) {
                                            isResizing.current = true;
                                            resizingLeftHeaderId.current = column.id;
                                            resizingRightHeaderId.current = shownColumns[index+1]['id'];
                                            lastDownX.current = e.clientX;
                                        }
                                    }}
                                    key={index}
                                />
                            )
                        }
                        </React.Fragment>
                    );    
                })
            )
        }
        else {
            return (
                columns.map((column, index) => {
                    const width = columnsWidths ? columnsWidths.find(col => col.id === column.id)?.width : column.width;
                    return (
                        <React.Fragment key={column.id}>
                        <SMCTableHeaderColumn
                            column={column}
                            width={width}
                            setSort={setSort}
                            sortBasis={sortBasis}
                            sortDirection={sortDirection}
                            className={classes.cell}
                        />
                        {
                            // Cell separators, if isColumnsResizable, they are draggable too
                            index !== (columns.length - 1) && (
                                <td
                                    className={clsx(classes.cellSeparator, isColumnsResizable && classes.resizable)}
                                    onMouseDown={e => {
                                        if (isColumnsResizable) {
                                            isResizing.current = true;
                                            resizingLeftHeaderId.current = column.id;
                                            resizingRightHeaderId.current = columns[index+1].id;
                                            lastDownX.current = e.clientX;
                                        }
                                    }}
                                    key={index}
                                />
                            )
                        }
                        </React.Fragment>
                    );
                })
            );
        }
    }, [columns, columnsWidths, columnsState, sortBasis, setSort, sortDirection, isColumnsResizable, classes]);

    return (
        <div className={classes.outerContainer} style={{ width: outerWidth }}>
            <div
                className={classes.innerContainer}
                style={{ width: innerWidth }}
                ref={innerRef}
            >
                <table
                    cellPadding="3"
                    cellSpacing="0"
                    className={classes.table}
                    style={{ width: innerWidth }}
                >
                    <tbody>
                        <tr style={{ width: innerWidth }}>
                            {
                                columnsState && setColumnsState && (
                                    <ClickAwayListener onClickAway={() => setIsEditTableProfileBoxOpen(false)}>
                                        <td style={{ width: 12, padding: 0, paddingLeft: 4 }} className={classes.cell}>
                                            <FontAwesomeIcon icon="bars" cursor="pointer" onClick={() => setIsEditTableProfileBoxOpen(open => !open)} />
                                            <div style={{ position: 'absolute', display: isEditTableProfileBoxOpen ? 'block' : 'none' }}>
                                                <div style={{ position: 'relative', zIndex: '100', right: '5px', top: '6px' }}>
                                                    <SMCTableHeaderMenu
                                                        columns={columns}
                                                        columnsState={columnsState}
                                                        setColumnsState={setColumnsState}
                                                        setDefaultColumnsState={setDefaultColumnsState}
                                                        setDefaultWidths={setDefaultWidths}
                                                        maxHeight={tableFullHeight-height}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </ClickAwayListener>
                                )
                            }
                            {
                                hasCheckboxColumn && (
                                    <SMCTableCheckAll
                                        hasCheckAll={hasCheckAll}
                                        isChecked={isAllChecked}
                                        onChange={checkAllOnChange}
                                        className={classes.cell}
                                    />
                                )
                            }
                            { header }
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const useStyles = makeStyles({
    outerContainer: {
        background: '#2F3F57',
        display: 'flex'
    },
    innerContainer: {
        overflow: 'hidden',
        borderRight: '1px solid #435672'
    },
    table: {
        tableLayout: 'fixed',
        width: 'fit-content',
        userSelect: 'none'
    },
    row: {
        display: 'table',
        tableLayout: 'fixed'
    },
    cell: {
        color: '#E8F1FF',
        fontWeight: '600',
        borderLeft: '#425673 1px solid',
        paddingLeft: '7px',
        paddingRight: '7px',
        height: props => props.height || '32px',
        boxSizing: 'border-box',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    },
    cellSeparator: {
        width: '3px',
        boxSizing: 'border-box',
        height: '100%',
        padding: 0,
    },
    resizable: {
        cursor: 'col-resize'
    },
})

SMCTableHeader.propTypes = {
    innerWidth: PropTypes.number.isRequired,
    outerWidth: PropTypes.number.isRequired,
    isAllChecked: PropTypes.bool,
    hasCheckAll: PropTypes.bool,
    hasCheckboxColumn: PropTypes.bool,
    columns: PropTypes.array.isRequired,
    onEndResizingColumns: PropTypes.func,
    setSort: PropTypes.func,
    sortBasis: PropTypes.string,
    sortDirection: PropTypes.string
}

export default React.memo(SMCTableHeader);